<template>
  <div>
    <birthday-chip :date-of-birth="dob" />
    <a :href="profileLink" target="_blank" class="secondary--text text-body-1 text-decoration-none">
      <strong :class="{ strike: strikeThrough && subscriptionEndDate }">{{ clientName }}</strong>
    </a>

    <div v-if="subscriptionSurveyId">
      <a
        :href="`/reporting/survey-results/${subscriptionSurveyId}`"
        target="_blank"
        class="caption text-decoration-none"
      >
        Survey
      </a>
    </div>

    <div class="d-flex">
      <v-btn v-if="lastActive" icon small>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" slot="activator">
              <v-icon size="18" :color="inactiveColor">mdi-account-clock</v-icon>
            </div>
          </template>
          <span>Last active: {{ lastActive | moment("from") }}</span>
        </v-tooltip>
      </v-btn>
      <v-btn icon small :href="contactLink" target="_blank">
        <v-tooltip bottom @click="contactLink" target="_blank">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" slot="activator">
              <v-icon size="18">mdi-text-account</v-icon>
            </div>
          </template>
          <span>
            View data
            <v-icon color="white">mdi-open-in-new</v-icon>
          </span>
        </v-tooltip>
      </v-btn>
      <v-btn v-if="subscriptionEndDate" icon small>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" slot="activator">
              <v-icon :color="subEndColor" size="18">mdi-school</v-icon>
            </div>
          </template>
          <span class="d-block">Ends: {{ subscriptionEndDate | moment("YYYY-MM-DD") }}</span>
          <span class="d-block">({{ subscriptionEndDate | moment("from") }})</span>
          <span
            >Transitioning to <strong>{{ nextSubscription }}</strong></span
          >
        </v-tooltip>
      </v-btn>
      <v-btn icon small @click="copyEmail()">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" slot="activator">
              <v-icon :color="copyText == 'Copied' ? 'navy' : null" size="18">mdi-content-copy</v-icon>
            </div>
          </template>
          <span>{{ copyText }}</span>
        </v-tooltip>
      </v-btn>

      <div v-if="showUpcomingPayment">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" slot="activator">
              <v-icon color="red" size="32">mdi-currency-usd</v-icon>
            </div>
          </template>
          <span
            >{{ upcomingPayment.type }} payment due on {{ $moment(upcomingPayment.date).format("ddd, DD MMM") }}</span
          >
        </v-tooltip>
      </div>
    </div>
    <billing-chip v-if="billingDate" :billingDate="billingDate" />

    <div v-if="coachingChatId">
      <v-btn x-small :href="`${$appEndPoint}/chats/${coachingChatId}`" target="_blank" outlined color="secondary">
        Coach <v-icon x-small color="navy" class="ml-1">mdi-chat</v-icon>
        <v-icon v-if="oustandingMessage" size="10" color="red">mdi-circle-slice-8</v-icon>
      </v-btn>
    </div>
    <div v-else-if="clientId">
      <v-btn x-small @click="createCoachingChat" color="secondary" :loading="loading">
        Create Coach Chat
      </v-btn>
    </div>
    <div v-if="supportChat" class="mt-2">
      <v-btn x-small :href="`${$appEndPoint}/chats/${supportChat.id}`" target="_blank" outlined color="secondary">
        Support <v-icon x-small color="black" class="ml-1">mdi-chat</v-icon>
      </v-btn>
    </div>

    <div class="d-flex align-center my-1">
      <div v-if="typeof bhag == 'boolean'">
        <bhag :hasGoal="bhag" />
      </div>
      <!-- <div v-if="typeof checkinGoal == 'boolean'">
        <checkin-goal :hasGoal="checkinGoal" :agedGoal="false" />
      </div> -->
      <div v-if="weeksOnProgram">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip small v-bind="attrs" v-on="on" class="caption">{{ weeksOnProgram }} </v-chip>
          </template>
          <span>Weeks on Subscription</span>
        </v-tooltip>
      </div>
      <div v-if="recurringUserTodos && outstandingUserTodos" @click="todoDialog = true" class="ml-2">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              size="32"
              v-bind="attrs"
              v-on="on"
              :color="outstandingUserTodos.length == 0 && recurringUserTodos.length == 0 ? 'green' : 'orange'"
              >mdi-format-list-checks</v-icon
            >
          </template>
          <span>Outstanding tasks ({{ outstandingUserTodos.length + recurringUserTodos.length }})</span>
        </v-tooltip>
      </div>
    </div>

    <v-dialog v-model="todoDialog" width="600">
      <v-card>
        <v-card-title>
          Tasks for {{ clientName }}
          <v-btn small class="ml-4" color="secondary" @click="showForm = !showForm"
            ><span v-if="!showForm">+ Add</span><span v-else>- Hide</span></v-btn
          >
        </v-card-title>

        <v-row v-if="showForm" class="px-4"
          ><v-col>
            <p>Choose your task type:</p>
            <v-radio-group v-model="data.scheduleType" row>
              <v-radio label="Once off" value="ONCE_OFF"></v-radio>
              <v-radio label="Recurring" value="RECURRING"></v-radio>
            </v-radio-group>

            <p v-if="data.scheduleType == 'ONCE_OFF'" class="text-body-2">
              This task can only be completed once, on a single day
            </p>
            <p v-else-if="data.scheduleType == 'RECURRING'" class="text-body-2">
              An example would be if you want your client to be off their phone after 6pm for every day for a week
            </p>

            <div v-if="data.scheduleType">
              <ValidationObserver ref="observer">
                <v-form @submit.prevent="submit">
                  <ValidationProvider id="title" v-slot="{ errors }" name="title" rules="required">
                    <v-text-field v-model.trim="data.title" label="Title" :error-messages="errors" />
                  </ValidationProvider>
                  <ValidationProvider id="description" v-slot="{ errors }" name="description" rules="required">
                    <v-textarea rows="2" v-model.trim="data.description" label="Description" :error-messages="errors" />
                  </ValidationProvider>
                  <v-text-field v-model.trim="data.youtube" label="YouTube link (optional)" />

                  <span v-if="data.scheduleType == 'RECURRING'">
                    <ValidationProvider id="startDate" v-slot="{ errors }" name="startDate" rules="required">
                      <v-menu
                        v-model="menu1"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="data.startDate"
                            label="Start Date"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors"
                          ></v-text-field>
                        </template>
                        <v-date-picker v-model="data.startDate" @input="menu1 = false"></v-date-picker>
                      </v-menu>
                    </ValidationProvider>

                    <ValidationProvider v-slot="{ errors }" rules="required">
                      <v-menu
                        v-model="menu2"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="data.endDate"
                            label="End Date"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors"
                          ></v-text-field>
                        </template>
                        <v-date-picker v-model="data.endDate" @input="menu2 = false"></v-date-picker>
                      </v-menu>
                    </ValidationProvider>
                  </span>

                  <v-btn small type="submit" color="secondary" class="mt-2">
                    Create Todo
                  </v-btn>
                </v-form>
              </ValidationObserver>
            </div>
          </v-col></v-row
        >
        <v-row class="mx-3">
          <v-col>
            <div v-if="outstandingUserTodos">
              <span class="font-weight-bold">Outstanding once off tasks ({{ outstandingUserTodos.length }}):</span>
              <ul>
                <li v-for="todo in outstandingUserTodos" :key="todo.id">
                  <div class="d-flex">
                    <div>
                      <span class="text-body-2 ">{{ todo.title }}</span>
                      <div>
                        <small class="darker-grey--text" v-html="todo.description" />
                      </div>
                    </div>
                    <div>
                      <v-icon class="ml-4" @click="deleteTodo(todo.id)" color="red" size="18">mdi-delete</v-icon>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div v-if="recurringUserTodos">
              <span class="font-weight-bold">Current Recurring tasks ({{ recurringUserTodos.length }}):</span>
              <ul>
                <li v-for="todo in recurringUserTodos" :key="todo.id">
                  <div class="d-flex">
                    <div>
                      <span class="text-body-2 "
                        >{{ todo.title }} -
                        <span class="darker-grey--text"
                          >{{ todo.startDate | moment("ddd, DD MMM") }} -
                          {{ todo.endDate | moment("ddd, DD MMM") }}</span
                        ></span
                      >
                      <div>
                        <small class="darker-grey--text" v-html="todo.description" />
                      </div>

                      <div v-for="userTodo in todo.usertodoSet" :key="userTodo.id">
                        <small
                          >{{ userTodo.completedDate | moment("ddd, DD MMM YYYY") }} -
                          <span
                            :class="{
                              'green--text': userTodo.type === 'ACHIEVED',
                              'orange--text': userTodo.type !== 'ACHIEVED',
                            }"
                            >{{ userTodo.type }}</span
                          ></small
                        >
                      </div>
                    </div>
                    <div>
                      <v-icon class="ml-4" @click="deleteTodo(todo.id)" color="red" size="18">mdi-delete</v-icon>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div v-if="completedTodosForUser" class="mt-4">
              <span class="font-weight-bold">Completed tasks ({{ completedTodosForUser.length }}):</span>
              <ul>
                <li v-for="todo in completedTodosForUser" :key="todo.id">
                  <span class="text-body-2">{{ todo.title }}</span> -
                  <small>{{ todo.completedAt | moment("ddd, DD MMM YYYY") }}</small>
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { OUTSTANDING_TODOS_QUERY, COMPLETED_TODOS_QUERY, RECURRING_TODOS_QUERY } from "@/graphql/queries/user";
import { CREATE_CLUB_COACH_CHAT_MUTATION } from "@/graphql/mutations/chat";
import { DELETE_TODO_MUTATION, UPSERT_TODO_MUTATION } from "@/graphql/mutations/todo";
import BirthdayChip from "../BirthdayChip";
import BillingChip from "../BillingChip";
import Bhag from "@/components/dashboard/components/columns/Bhag";
//import CheckinGoal from "@/components/dashboard/components/columns/CheckinGoal";

export default {
  name: "ClientName",
  props: {
    clientId: {
      type: [Number, String],
      required: false,
    },
    dob: {
      type: String,
      required: false,
    },
    profileLink: {
      type: String,
      required: true,
    },
    subscriptionEndDate: {
      type: String,
      required: false,
    },
    strikeThrough: {
      type: Boolean,
      default: true,
    },
    clientName: {
      type: String,
      required: false,
    },
    username: {
      type: String,
      required: false,
    },
    contactLink: {
      type: String,
      required: true,
    },
    billingDate: {
      type: String,
      required: false,
    },
    lastActive: {
      type: String,
      required: false,
    },
    nextSubscription: {
      type: String,
      default: "None",
    },
    upcomingPayment: {
      type: Object,
    },
    coachingChat: {
      type: Object,
    },
    supportChat: {
      type: Object,
    },
    coachId: {
      type: String,
      required: true,
    },
    subscriptionSurveyId: {
      type: [Number, String],
      required: false,
    },
    bhag: {
      type: Boolean,
      default: null,
      required: false,
    },
    checkinGoal: {
      type: Boolean,
      default: null,
      required: false,
    },
    weeksOnProgram: {
      type: [Number, String],
      required: false,
    },
  },
  components: {
    BirthdayChip,
    BillingChip,
    Bhag,
    //CheckinGoal,
  },
  data() {
    return {
      copyText: "Copy email",
      loading: false,
      createdChatId: null,
      todoDialog: false,
      showForm: false,

      data: {
        userId: this.clientId,
        title: null,
        description: null,
        youtube: null,
        startDate: null,
        endDate: null,
        scheduleType: null,
      },
      menu1: false,
      menu2: false,
    };
  },
  methods: {
    submit() {
      if (this.data.scheduleType == "RECURRING" && (!this.data.startDate || !this.data.endDate)) {
        this.$toasted.error("Please select a start and end date for your recurring task ", {
          duration: 2000,
          position: "bottom-center",
        });

        return;
      }
      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          this.$apollo.mutate({
            mutation: UPSERT_TODO_MUTATION,
            variables: {
              data: this.data,
            },
            refetchQueries: ["outstandingUserTodos", "recurringUserTodos"],
          });
          this.data = {
            userId: this.clientId,
            title: null,
            description: null,
            youtube: null,
            startDate: null,
            endDate: null,
            scheduleType: null,
          };
          this.showForm = false;
        }
      });
    },
    deleteTodo: async function(todoId) {
      await this.$apollo.mutate({
        mutation: DELETE_TODO_MUTATION,
        variables: {
          todoId: todoId,
          userId: this.clientId,
        },
        refetchQueries: ["outstandingUserTodos"],
      });
    },
    async copyEmail() {
      await navigator.clipboard.writeText(this.username);
      this.copyText = "Copied";
    },
    async createCoachingChat() {
      this.loading = true;
      const result = await this.$apollo.mutate({
        mutation: CREATE_CLUB_COACH_CHAT_MUTATION,
        variables: {
          userId: this.clientId,
          coachId: this.coachId,
        },
      });
      this.loading = false;
      this.createdChatId = result.data?.createCoachingChatMutation?.id;
    },
  },
  computed: {
    coachingChatId() {
      // if we have just created a chat, then use that ID, else fall through.
      if (this.createdChatId) {
        return this.createdChatId;
      }
      return this.coachingChat?.id;
    },
    oustandingMessage() {
      if (!this.coachingChat) {
        return false;
      }
      let coachChat = this.coachingChat.userchatSet.find((userChat) => userChat.user.id == this.coachId);
      let lastMessage = this.coachingChat.lastMessage;
      if (lastMessage && lastMessage.user.id != this.coachId) {
        if (coachChat && coachChat.lastViewedTimestamp == null) {
          return true;
        } else if (
          coachChat &&
          coachChat.lastViewedTimestamp &&
          this.$moment(lastMessage.createdAt) >= this.$moment(coachChat.lastViewedTimestamp)
        ) {
          return true;
        }
      }
      return false;
    },
    showUpcomingPayment() {
      if (!this.upcomingPayment || this.upcomingPayment?.type == "MONTHLY") {
        return false;
      }
      return this.$moment(this.upcomingPayment.date).diff(this.$moment(), "days") <= 5;
    },
    inactiveColor() {
      let inactive = this.$moment().diff(this.lastActive, "hours") > 48;

      return inactive ? "red" : null;
    },
    subEndColor() {
      if (this.nextSubscription == "None" && this.$moment().diff(this.subscriptionEndDate, "days") <= 5) return "red";
      else if (this.nextSubscription.includes("1-on-1", "Club")) return "green";

      return null;
    },
  },
  apollo: {
    outstandingUserTodos: {
      query: OUTSTANDING_TODOS_QUERY,
      variables() {
        return {
          userId: this.clientId,
        };
      },
      fetchPolicy: "cache-and-network",
    },
    completedTodosForUser: {
      query: COMPLETED_TODOS_QUERY,
      variables() {
        return {
          userId: this.clientId,
        };
      },
      fetchPolicy: "cache-and-network",
    },
    recurringUserTodos: {
      query: RECURRING_TODOS_QUERY,
      variables() {
        return {
          userId: this.clientId,
          anchorDate: this.$moment().format("YYYY-MM-DD"),
        };
      },
      fetchPolicy: "cache-and-network",
    },
  },
};
</script>
